import * as React from 'react';

const STORAGE_KEY = 'netWorth.revealed';

const loadRevealed = (): boolean => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // sessionStorage may be unavailable; start hidden.
    return false;
  }
};

/**
 * Forget a reveal. Called on logout: the flag is not tied to a user, so
 * without this a different user signing in within the same tab would open
 * the page already revealed.
 */
export const clearRevealed = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort; sessionStorage may be unavailable.
  }
};

/**
 * Whether the net worth page shows dollar amounts. Hidden by default so the
 * page is safe to open in public, and remembered only for the browser
 * session: a reveal survives navigating away and back, never a relaunch.
 */
export const useRevealed = (): [boolean, (revealed: boolean) => void] => {
  const [revealed, setRevealedState] = React.useState(loadRevealed);
  const setRevealed = React.useCallback((next: boolean) => {
    setRevealedState(next);
    try {
      if (next) {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Best-effort; the reveal still applies until the page unmounts.
    }
  }, []);
  return [revealed, setRevealed];
};
